import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useLocalStorage from '@/hooks/useLocalStorage';
import {
  defaultProducts,
  defaultCategories,
  defaultOrders,
  type AdminProduct,
  type AdminCategory,
  type AdminOrder,
} from '@/mocks/adminData';

function DashboardPage() {
  const navigate = useNavigate();
  const [products] = useLocalStorage<AdminProduct[]>('admin_products', defaultProducts);
  const [categories] = useLocalStorage<AdminCategory[]>('admin_categories', defaultCategories);
  const [orders] = useLocalStorage<AdminOrder[]>('admin_orders', defaultOrders);

  const stats = useMemo(() => {
    const active = products.filter((p) => p.active);
    const featured = active.filter((p) => p.featured);
    const recent = [...products].sort((a, b) => parseInt(b.id.split('-')[1], 10) - parseInt(a.id.split('-')[1], 10)).slice(0, 5);
    return {
      totalProducts: active.length,
      totalCategories: categories.filter((c) => c.active).length,
      totalFeatured: featured.length,
      totalOrders: orders.length,
      recent,
    };
  }, [products, categories, orders]);

  const statusLabels: Record<string, { label: string; className: string }> = {
    'novo': { label: 'Novo', className: 'bg-blue-100 text-blue-700' },
    'em-atendimento': { label: 'Em atendimento', className: 'bg-amber-100 text-amber-700' },
    'finalizado': { label: 'Finalizado', className: 'bg-green-100 text-green-700' },
    'cancelado': { label: 'Cancelado', className: 'bg-red-100 text-red-700' },
  };

  const summaryCards = [
    { label: 'Produtos ativos', value: stats.totalProducts, icon: 'ri-shopping-bag-2-line', color: 'bg-primary-100 text-primary-600', path: '/admin/produtos' },
    { label: 'Categorias', value: stats.totalCategories, icon: 'ri-price-tag-3-line', color: 'bg-accent-100 text-accent-600', path: '/admin/categorias' },
    { label: 'Em destaque', value: stats.totalFeatured, icon: 'ri-star-line', color: 'bg-amber-100 text-amber-600', path: '/admin/produtos' },
    { label: 'Pedidos', value: stats.totalOrders, icon: 'ri-inbox-line', color: 'bg-secondary-100 text-secondary-600', path: '/admin/pedidos' },
  ];

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Dashboard</h1>
        <p className="text-sm text-foreground-500 mt-1">Bem-vindo ao painel administrativo da Maçã Verde.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {summaryCards.map((card) => (
          <button
            key={card.label}
            type="button"
            onClick={() => navigate(card.path)}
            className="bg-white rounded-xl border border-background-200 p-4 md:p-5 text-left hover:border-primary-200 transition-colors cursor-pointer"
          >
            <div className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center mb-3`}>
              <i className={`${card.icon} text-lg`} />
            </div>
            <p className="text-2xl md:text-3xl font-bold text-foreground-900">{card.value}</p>
            <p className="text-xs text-foreground-500 mt-1">{card.label}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Products */}
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base font-semibold text-foreground-900">Últimos produtos adicionados</h2>
            <button
              type="button"
              onClick={() => navigate('/admin/produtos')}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              Ver todos
            </button>
          </div>
          {stats.recent.length === 0 ? (
            <p className="text-sm text-foreground-500 py-6 text-center">Nenhum produto cadastrado ainda.</p>
          ) : (
            <div className="space-y-3">
              {stats.recent.map((p) => (
                <div key={p.id} className="flex items-center gap-3">
                  <img src={p.mainImage} alt={p.name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-800 truncate">{p.name}</p>
                    <p className="text-xs text-foreground-500">R$ {p.price.toFixed(2)}</p>
                  </div>
                  {!p.active && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">Inativo</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Tips & Recent Orders */}
        <div className="space-y-6">
          {/* Tip */}
          <div className="bg-primary-50 border border-primary-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-lightbulb-line text-sm text-primary-600" />
              </div>
              <div>
                <h3 className="font-heading text-sm font-semibold text-primary-800 mb-1">Dica rápida</h3>
                <p className="text-sm text-primary-700">
                  Atualize suas coleções com frequência para manter o catálogo atrativo. Produtos em destaque aparecem primeiro para os clientes.
                </p>
              </div>
            </div>
          </div>

          {/* Recent Orders */}
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-base font-semibold text-foreground-900">Pedidos recentes</h2>
              <button
                type="button"
                onClick={() => navigate('/admin/pedidos')}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                Ver todos
              </button>
            </div>
            {orders.length === 0 ? (
              <p className="text-sm text-foreground-500 py-6 text-center">Nenhum pedido recebido ainda.</p>
            ) : (
              <div className="space-y-2.5">
                {orders.slice(0, 4).map((order) => (
                  <div key={order.id} className="flex items-center justify-between py-2 border-b border-background-100 last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-800 truncate">{order.customerName}</p>
                      <p className="text-xs text-foreground-500">{order.date} — {order.products.length} produto(s)</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-semibold text-foreground-800">R$ {order.total.toFixed(2)}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusLabels[order.status]?.className || ''}`}>
                        {statusLabels[order.status]?.label || order.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;